/*
 * KSPEK :: cart-vibe-upsell
 *
 * Custom element powering the "Complete the look" bag upsell in BOTH the
 * cart-notification popup and the /cart page. Self-initializes whenever it is
 * inserted into the DOM (including via innerHTML when CartNotification or
 * CartItems re-render their sections), so no manual re-binding is needed.
 *
 * Behaviour:
 *  - "Add" does an AJAX POST to /cart/add.js, requesting the relevant sections
 *    back so the cart UI refreshes without a page reload.
 *  - After a successful add the cart re-renders; the upsell re-evaluates server
 *    side and hides itself (cart now contains a bag-class item) — and the
 *    bundle's automatic Buy-X-Get-Y discount kicks in at checkout.
 *  - Every product is stock=1, so a bag can sell out between view and add.
 *    Because we now render a CHOICE of several bag cards, a failed add
 *    (response.status set, e.g. 422) is caught and ONLY the clicked card is
 *    removed; the other cards (and the "Shop all bags" link) stay intact. If
 *    that was the last card, the whole block hides. The shopper never sees a
 *    broken "Add" and never loses the other options.
 */
if (!customElements.get('cart-vibe-upsell')) {
  customElements.define(
    'cart-vibe-upsell',
    class CartVibeUpsell extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.surface = this.dataset.surface || 'notification';
        this.errorEl = this.querySelector('.cart-upsell__error');
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(evt) {
        const btn = evt.target.closest('[data-upsell-add]');
        if (!btn || this.loading) return;
        evt.preventDefault();
        this.addVariant(btn);
      }

      // Sections to refresh after the add, by surface. Notification uses the
      // cart-notification element's own section list (so the popup, bubble and
      // upsell all update); the cart page refreshes the line items, footer and
      // this block.
      sectionsToRequest() {
        const cartNotification = document.querySelector('cart-notification');
        if (this.surface === 'cart') {
          return ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble', this.dataset.sectionId];
        }
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          // cartItemKey isn't known until render; request by section id only.
          return cartNotification.getSectionsToRender().map((s) => s.id);
        }
        return ['cart-notification-product', 'cart-notification-upsell', 'cart-notification-button', 'cart-icon-bubble'];
      }

      setLoading(btn, on) {
        this.loading = on;
        btn.classList.toggle('loading', on);
        btn.setAttribute('aria-disabled', on ? 'true' : 'false');
        const spinner = btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = !msg;
      }

      // Stock=1 sell-out (or any add failure): drop the dead card. If other
      // matched bags remain, keep going; otherwise hide the whole block.
      degradeAfterFailure(btn, msg) {
        const item = btn.closest('[data-upsell-item]');
        if (item) item.remove();
        const remaining = this.querySelectorAll('[data-upsell-item]').length;
        if (remaining === 0) {
          // Nothing left to offer — remove silently rather than show a broken UI.
          this.remove();
        } else if (msg) {
          this.showError(msg);
        }
      }

      addVariant(btn) {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        this.showError('');
        this.setLoading(btn, true);

        const body = {
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.sectionsToRequest(),
          sections_url: window.location.pathname,
        };

        fetch(`${window.routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(body),
        })
          .then((res) => res.json().then((data) => ({ ok: res.ok, status: res.status, data })))
          .then(({ ok, data }) => {
            // Shopify returns a `status` field on add errors (e.g. sold out).
            if (!ok || (data && data.status)) {
              const msg =
                (data && (data.description || data.message)) ||
                (window.cartStrings && window.cartStrings.error) ||
                'Sorry, that just sold out.';
              this.degradeAfterFailure(btn, msg);
              return;
            }
            this.afterAdd(data);
          })
          .catch(() => {
            this.degradeAfterFailure(
              btn,
              (window.cartStrings && window.cartStrings.error) || 'Something went wrong — please try again.'
            );
          })
          .finally(() => {
            // btn may have been removed on failure; guard.
            if (btn.isConnected) this.setLoading(btn, false);
          });
      }

      afterAdd(addResponse) {
        const sections = addResponse.sections || {};

        if (this.surface === 'cart') {
          // Refresh the cart-page sections we asked for.
          this.replaceSection('main-cart-items', sections['main-cart-items'], 'cart-items', true);
          this.replaceSection('main-cart-footer', sections['main-cart-footer'], '.js-contents');
          this.replaceSection('cart-icon-bubble', sections['cart-icon-bubble'], '.shopify-section', false, true);
          // Re-render our own block last (it will hide now a bag is in cart).
          this.replaceSelf(sections[this.dataset.sectionId]);
        } else {
          // Notification: let the existing component own the popup render. It
          // re-renders product + upsell + button + bubble and opens the popup.
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents(addResponse);
          }
        }

        // Notify the rest of the theme (analytics, drawer mirrors, etc.).
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-vibe-upsell',
            cartData: addResponse,
            variantId: undefined,
          });
        } catch (e) {}
      }

      sectionInner(html, selector) {
        if (!html) return null;
        const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return found ? found.innerHTML : null;
      }

      replaceSection(containerId, html, selector, replaceInner, isWrapper) {
        const container = document.getElementById(containerId);
        if (!container || !html) return;
        const inner = this.sectionInner(html, isWrapper ? '.shopify-section' : selector);
        if (inner == null) return;
        if (replaceInner) {
          // <cart-items> case: swap the whole inner of the custom element.
          const el = container.querySelector(selector) || container;
          el.innerHTML = inner;
        } else {
          const target = container.querySelector(selector) || container;
          target.innerHTML = inner;
        }
      }

      replaceSelf(html) {
        const container = document.getElementById('cart-page-upsell');
        if (!container) return;
        const inner = this.sectionInner(html, '.shopify-section');
        container.innerHTML = inner == null ? '' : inner;
      }
    }
  );
}
